import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Modal, Breadcrumb, Typography, Spin, Select, DatePicker, Upload } from 'antd';

import dayjs from 'dayjs';
import {
  backButton,
  formItemLayout,
  handleBackendFormErrors,
  tailFormItemLayout,
  unexpectedError,
  getBase64,
  beforeUpload,
  onRemove,
} from '../../common_functions';
import config from '../../config';
import UploadTrigger from '../../common_functions/UploadTrigger';

const Books = props => {
  const { section, id } = useParams();
  const [book, setBook] = useState({});
  const [loading, setLoading] = useState(false);
  const [artist, setArtist] = useState([]);
  // Preview Image
  const [previewVisible, setPreviewVisible] = useState(false);
  const [imgPreview, setImgPreview] = useState('');
  const [previewTitle, setPreviewTitle] = useState(false);
  const [image, setImage] = useState(null);

  const formRef = useRef(null);

  const getArtists = async () => {
    let { data: artistFullName } = await props.getItems('Artist', {
      fields: ['id', 'First_Name', 'last_name'],
      sort: '-id',
      limit: -1,
    });
    setArtist(artistFullName);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        await getArtists();
        if (id !== 'new') {
          let { data } = await props.getItem('books', id, {
            fields: ['*', 'cover_photo.id', 'cover_photo.filename_download'],
          });
          if (data?.cover_photo?.id) {
            const { filename_download, id: fileID } = data.cover_photo;
            const fileData = await props.getFile(fileID);
            setImage([
              {
                uid: fileID,
                name: filename_download,
                url: fileData,
              },
            ]);
            data.cover_photo = fileID;
          }
          data.date_of_publication = data.date_of_publication ? dayjs(data.date_of_publication) : null;
          setBook(data);
          formRef.current.setFieldsValue(data);
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  let onFinish = async values => {
    try {
      setLoading(true);

      if (values?.cover_photo?.fileList?.[0]?.originFileObj) {
        const { fileList } = values.cover_photo;
        const formData = new FormData();
        formData.append('folder', process.env.book_images_folder);
        formData.append('file', fileList[0].originFileObj);

        let {
          data: { id: fileID },
        } = await props.saveFile(formData, book?.cover_photo);
        values.cover_photo = fileID;
      }
      let newState = { ...book, ...values };
      let response = await props.saveItem('books', newState);
      if (response) {
        notification['success']({
          message: 'Books saved successfully!',
          duration: 2,
        });
        backButton(props, `/bibliography/books`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };

  const handlePreview = async file => {
    setImgPreview('');
    setImgPreview(file.url);
    setPreviewTitle(file.name);
    setPreviewVisible(true);
  };

  const handleCancel = () => setPreviewVisible(false);
  /**Handle file change**/
  const uploadFile = async ({ file, fileList }) => {
    try {
      setLoading(true);
      if (!(file instanceof File)) return;
      const url = await getBase64(fileList[0].originFileObj);
      setImage([
        {
          uid: '-1',
          name: file.name,
          status: 'done',
          url: url,
        },
      ]);
      setLoading(false);
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    }
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Bibliography", href: "/bibliography/books" }, { title: id != 'new' ? `Edit Book: ${id}` : 'Add Book' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id != 'new' ? `Edit Book: ${id}` : 'Add Book'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="book_title"
                    label="Book Title"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Book title is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter book title" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="publisher"
                    label="Publisher"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Publisher is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter book title" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="artist"
                    label="Artist"
                    rules={[
                      {
                        required: true,
                        message: 'Publisher is required!',
                      },
                    ]}
                  >
                    <Select showSearch allowClear placeholder="Select artist" optionFilterProp="children">
                      {artist?.map(val => (
                        <Select.Option key={val.id} value={val.id}>
                          {val.First_Name} {val.last_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="author"
                    label="Author"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Author is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter author" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="editor" label="Editor">
                    <Input placeholder="Enter editor" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="edition"
                    label="Edition #"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Edition # is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter editor #" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="isbn"
                    label="ISBN #"
                    rules={[
                      {
                        whitespace: true,
                        required: false,
                        message: 'ISBN # is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter ISBN #" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="date_of_publication"
                    label="Date of Publication"
                    rules={[
                      {
                        required: true,
                        message: 'Date of publication is required!',
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter date of publication" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="cover_photo" label="Cover Image">
                    <Upload
                      name="file"
                      listType="picture"
                      className="create_object"
                      accept={config.FileType.bookImage.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'bookImage');
                      }}
                      onRemove={onRemove}
                      showUploadList={{
                        showRemoveIcon: false,
                      }}
                      onPreview={handlePreview}
                      maxCount={1}
                      multiple={false}
                      onChange={uploadFile}
                      fileList={image}
                    >
                      <UploadTrigger fileType="bookImage" />
                    </Upload>
                  </Form.Item>
                </Col>
                <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel} width={800}>
                  <div style={{ textAlign: 'center' }}>
                    {!!imgPreview && (
                      <img
                        alt="No Image"
                        style={{
                          width: '100%',
                          height: 'auto',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                        src={imgPreview}
                      />
                    )}
                  </div>
                </Modal>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/bibliography/books')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(props.roleType, props.tabs_permissions.bibliography_books, !!book ? book.id : '')}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default Books;
